import AboutTemplate from "../../components/about/AboutTemplate";
import Page from "../Page";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Section from "../../components/about/Section";
import ResponsiveImage from "../../components/about/ResponsiveImage";

const RecurringTasks = () => {
  return (
    <Page className="about-page recurring-tasks-page" pageKey="recurring-tasks">
      <AboutTemplate>
        <Typography variant="h1">Recurring Tasks</Typography>
        <Section>
          <Typography variant="p1">
            Recurring tasks are tasks that you need to do on a regular basis.
            They are a great way to keep track of things you do regularly, such
            as taking out the bins or watering your plants.
          </Typography>
          <ResponsiveImage
            pageKey="recurringTasks"
            imageName="recurring-task"
            size="large"
          />
        </Section>

        <Section title="Setting a task to recur">
          <Typography variant="p1">
            You can set a task to recur through either the{" "}
            <Link
              component={RouterLink}
              to="/about/features/tasks/create-a-task"
              variant="p1Link"
            >
              "Create Task"
            </Link>{" "}
            or "Edit Task" pages.
          </Typography>
          <Typography variant="p1">
            Tick the checkbox next to the "Recur" input to enable recurrence.
            This will open the Set Recurrence wizard, which guides you through
            the setup in two steps.
          </Typography>
          <ResponsiveImage
            pageKey="recurringTasks"
            imageName="recur-checkbox"
            size="medium"
          />
        </Section>

        <Section title="Step 1: Choose recurrence type">
          <Typography variant="p1">
            The first step asks you to choose how the task should recur. There
            are two options:
          </Typography>
          <Typography variant="p1">
            <strong>Recur on a specific day</strong> - The task is added back to
            your list on the same day each week, month, or year. This is best
            for tasks that need to happen on a particular day, such as "Take
            bins out every Monday".
          </Typography>
          <Typography variant="p1">
            <strong>Recur after completion</strong> - A countdown starts after
            you complete the task. This is best for tasks where the timing
            depends on when you last did them, such as "Clean bathroom every 2
            weeks after you finish it".
          </Typography>
          <ResponsiveImage
            pageKey="recurringTasks"
            imageName="step-1-type"
            size="medium"
          />
        </Section>

        <Section title="Step 2: Configure the interval">
          <Typography variant="p1">
            The second step lets you set how often the task should recur. You
            can choose an interval count and a time unit (days, weeks, or
            months).
          </Typography>
          <ResponsiveImage
            pageKey="recurringTasks"
            imageName="step-2-configure"
            size="medium"
          />
        </Section>

        <Section title="Choosing a day">
          <Typography variant="p1">
            If you selected "Recur on a specific day" and chose a weekly
            interval, you will be asked to pick which day of the week the task
            should recur on.
          </Typography>
          <ResponsiveImage
            pageKey="recurringTasks"
            imageName="day-of-week-selector"
            size="medium"
          />
          <Typography variant="p1">
            For monthly intervals, you can choose to recur on a specific date
            (such as the 15th of each month) or on a specific weekday (such as
            the first Monday of each month).
          </Typography>
          <ResponsiveImage
            pageKey="recurringTasks"
            imageName="monthly-options"
            size="medium"
          />
        </Section>

        <Section title="Summary preview">
          <Typography variant="p1">
            Before saving, the wizard shows a summary of your recurrence
            settings in plain language, so you can confirm everything looks
            right. For example, "Every 2 weeks on Monday" or "1 month after
            completion".
          </Typography>
          <ResponsiveImage
            pageKey="recurringTasks"
            imageName="summary-preview"
            size="medium"
          />
        </Section>

        <Section title="How recurring tasks work">
          <Typography variant="h3">Example</Typography>
          <Typography variant="p1">
            A task is set to recur on the 1st of every month.
          </Typography>
          <Typography variant="p1">
            The task is completed on the 10th of August and removed from your
            task list.
          </Typography>
          <Typography variant="p1">
            The task will remain completed for the rest of the month, but on the
            1st of September the task will be re-added to your task list.
          </Typography>
          <Typography variant="p1">
            This will repeat indefinitely until the task is deleted or
            recurrence is turned off for the task.
          </Typography>
        </Section>

        <Section title="Editing recurrence">
          <Typography variant="p1">
            You can change a task's recurrence settings at any time by clicking
            the recurrence field on the Edit Task page. This will re-open the
            Set Recurrence wizard where you can adjust the type, interval, or
            day.
          </Typography>
        </Section>

        <Section title="Removing recurrence">
          <Typography variant="p1">
            You can remove a task's recurrence by unticking the Recur checkbox
            on the Create Task or Edit Task page.
          </Typography>
          <Typography variant="p1">
            A task may either recur or have a deadline, not both. If you enable
            recurrence whilst a task has a deadline set, the deadline will be
            removed.
          </Typography>
        </Section>
      </AboutTemplate>
    </Page>
  );
};

export default RecurringTasks;
